import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { GRAY_BORDER, WHITE, interText } from "./styles";
import { gradeLabel, questionCountLabel } from "./warmUpSets";

const PROMPT_LINES = 8;
const PROMPT_LINE_HEIGHT = 22;
const TEXT_CARD_GAP = 4;
const IMAGE_CARD_GAP = 8;
const FOCUS_BAND = 4;
// Figma green/950: the outline of every sheet of the deck.
const CARD_STROKE = colors.nightlyWoods950;

const titleId = (set) => `warm-up-set-${set.id}-title`;
const footerId = (set) => `warm-up-set-${set.id}-footer`;

// One set as Figma's deck: two back sheets under a front button. The front is
// named after the set and described by its footer ("Grade 4-12, 5 Questions").
// When the first question has a stored image, it sits beside the question text;
// the image is decorative, and one that fails to load leaves a text card.
export const SetCard = ({ set, onOpen, cardRef }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = imageFailed ? null : set.first_question?.internal_image_url;

  return (
    <>
      <BackSheet aria-hidden="true" $offset={20} $width={403} />
      <BackSheet aria-hidden="true" $offset={10} $width={404} />
      <Front
        type="button"
        ref={cardRef}
        aria-labelledby={titleId(set)}
        aria-describedby={footerId(set)}
        onClick={() => onOpen?.(set)}
      >
        <TextColumn $gap={imageUrl ? IMAGE_CARD_GAP : TEXT_CARD_GAP}>
          <Title id={titleId(set)}>{set.name}</Title>
          {imageUrl ? (
            <ImageRow>
              <CardImage src={imageUrl} alt="" aria-hidden="true" onError={() => setImageFailed(true)} />
              <ImagePrompt>{set.first_question.prompt}</ImagePrompt>
            </ImageRow>
          ) : (
            <Prompt>{set.first_question?.prompt}</Prompt>
          )}
        </TextColumn>
        <Footer id={footerId(set)}>
          <FooterText>{gradeLabel(set)}</FooterText>
          <Divider aria-hidden="true" />
          <FooterText>{questionCountLabel(set.question_count)}</FooterText>
        </Footer>
      </Front>
    </>
  );
};

// The loading state's card: the same deck with an empty front.
export const SetCardSkeleton = () => (
  <>
    <BackSheet $offset={20} $width={403} />
    <BackSheet $offset={10} $width={404} />
    <SkeletonFront />
  </>
);

const BackSheet = styled.div`
  background: ${colors.milkGlass600};
  border: 1px solid ${CARD_STROKE};
  border-radius: 8px;
  box-sizing: border-box;
  height: 276px;
  left: ${({ $offset }) => $offset}px;
  position: absolute;
  top: ${({ $offset }) => $offset}px;
  width: ${({ $width }) => $width}px;
`;

const Front = styled.button`
  background: ${WHITE};
  border: 1px solid ${CARD_STROKE};
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 276px;
  justify-content: space-between;
  left: 0;
  margin: 0;
  padding: 15px;
  position: absolute;
  text-align: left;
  top: 0;
  width: 403px;

  &:hover {
    background: ${colors.blue50};
  }

  &:focus-visible {
    border-color: transparent;
    outline: ${FOCUS_BAND}px solid ${colors.blue800};
    outline-offset: 0;
  }
`;

const SkeletonFront = styled.div`
  background: ${WHITE};
  border: 1px solid ${CARD_STROKE};
  border-radius: 8px;
  box-sizing: border-box;
  height: 276px;
  left: 0;
  position: absolute;
  top: 0;
  width: 403px;
`;

const TextColumn = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap}px;
  min-width: 0;
  width: 100%;
`;

const Title = styled.span`
  ${interText(18, 26, 700)}
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Prompt = styled.span`
  ${interText(16, PROMPT_LINE_HEIGHT)}
  display: -webkit-box;
  -webkit-line-clamp: ${PROMPT_LINES};
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: ${PROMPT_LINES * PROMPT_LINE_HEIGHT}px;
`;

const ImageRow = styled.span`
  align-items: flex-start;
  display: flex;
  gap: 16px;
`;

const CardImage = styled.img`
  background: transparent;
  border: none;
  border-radius: 0;
  flex: none;
  height: 100px;
  object-fit: contain;
  object-position: 50% 50%;
  width: 100px;
`;

const ImagePrompt = styled(Prompt)`
  width: 255px;
`;

const Footer = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const FooterText = styled.span`
  ${interText(16, 22)}
`;

const Divider = styled.span`
  background: ${GRAY_BORDER};
  display: block;
  height: 21px;
  width: 1px;
`;
